const PROVIDER = { "@type": "LocalBusiness", name: "India Boats" };
const AREA_SERVED = ["Mumbai", "Goa"];

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "India Boats",
  legalName: "Aquartia Pvt. Ltd.",
  image: "https://www.indiaboats.in/assets/img/ib_red_logo.png",
  telephone: "+91-8591993301",
  email: "customercare@indiaboats.in",
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "803, Hubtown Viva, Western Express Highway, Jogeshwari East",
    addressLocality: "Mumbai",
    postalCode: "400060",
    addressCountry: "IN",
  },
  areaServed: AREA_SERVED,
  sameAs: [
    "https://www.facebook.com/indiaboats.in/",
    "https://x.com/india_boats",
    "https://www.linkedin.com/in/india-boats-866220114/",
    "https://www.youtube.com/channel/UC9sFJ9TzZTm9OupLqxd02wA",
  ],
};

export const buildServiceSchema = (serviceType, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType,
  provider: PROVIDER,
  areaServed: AREA_SERVED,
  description,
});

export const buildFaqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  })),
});

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "India Boats",
  legalName: "Aquartia Pvt. Ltd.",
  url: "https://www.indiaboats.in/",
  logo: "https://www.indiaboats.in/assets/img/ib_red_logo.png",
  email: "customercare@indiaboats.in",
  telephone: "+91-8591993301",
  sameAs: [
    "https://www.facebook.com/indiaboats.in/",
    "https://x.com/india_boats",
    "https://www.linkedin.com/in/india-boats-866220114/",
    "https://www.youtube.com/channel/UC9sFJ9TzZTm9OupLqxd02wA",
  ],
};

export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

const PRIVATE_YACHT_FAQS = [
  {
    question: "What types of private yachts can I book in Mumbai?",
    answer:
      "You can explore private yacht charter options in Mumbai across categories such as sail boats, compact yachts, mid size yachts, premium yachts and bigger sail yachts.",
  },
  {
    question: "How do I choose the right yacht category?",
    answer:
      "The right yacht depends on your group size, occasion, comfort preference and the type of private sea experience you want. Indiaboats can help recommend the best option.",
  },
  {
    question: "Are sail boats and yachts both available for private charter?",
    answer:
      "Yes. Indiaboats offers both sail boats and private yachts in Mumbai for guests looking for different styles of on-water experience.",
  },
  {
    question: "Is a compact yacht suitable for couples and small families?",
    answer:
      "Yes. Compact yachts are ideal for couples, small families and close groups looking for a private and comfortable yacht experience.",
  },
  {
    question: "What is the benefit of choosing a premium yacht?",
    answer:
      "Premium yachts offer more space, stronger comfort, better onboard experience and a more elevated private yacht charter feel.",
  },
  {
    question: "Can I book a private yacht for a birthday or anniversary?",
    answer:
      "Yes. Private yacht charters in Mumbai are a popular choice for birthdays, anniversaries and intimate celebrations.",
  },
  {
    question: "Is a private yacht charter in Mumbai safe?",
    answer:
      "Yes. Indiaboats places strong emphasis on trained crew, guest support and responsible operations.",
  },
  {
    question: "How do I enquire for a private yacht charter in Mumbai?",
    answer:
      "You can contact Indiaboats through the enquiry form or WhatsApp with your date, guest count and occasion.",
  },
];

const YACHT_PARTY_FAQS = [
  {
    question: "Can I book a specific yacht by name for a yacht party in Mumbai?",
    answer:
      "Yes. This page displays actual yacht names from the database so guests can browse, compare, and book a specific yacht directly.",
  },
  {
    question: "Will each yacht card have a direct booking link?",
    answer:
      "Yes. Each yacht listing card includes a direct 'Book Now' link along with a 'View Details' option to streamline your reservation process.",
  },
  {
    question: "What information should each yacht card show?",
    answer:
      "Each card shows the specific yacht name, real image, size dimensions, guest capacity, a short entertainment-focused description, and a direct booking link.",
  },
  {
    question: "Can I compare yachts by size and guest capacity?",
    answer:
      "Yes. The page is designed to help guests easily compare yacht options side-by-side based on size, guest capacity, and celebration suitability.",
  },
  {
    question: "Are these yacht party options suitable for birthdays and anniversaries?",
    answer:
      "Yes. Yacht parties in Mumbai are a highly popular choice for premium milestone birthdays, romantic anniversaries, and elite private celebrations.",
  },
  {
    question: "Can I book a yacht for a small private group?",
    answer:
      "Yes. Smaller yacht and compact cruiser options are ideal for intimate celebrations, close friend group gatherings, and private family dinners.",
  },
  {
    question: "Is a yacht party in Mumbai safe?",
    answer:
      "Yes. Indiaboats places a strong emphasis on a highly trained marine crew, constant guest support, and completely responsible, certified operations.",
  },
  {
    question: "How do I enquire if I am not sure which yacht to choose?",
    answer:
      "You can instantly contact Indiaboats through the on-page enquiry form or via WhatsApp with your target date, guest count, and celebration type for personalized recommendations.",
  },
];

const SPEED_BOAT_FAQS = [
  {
    question: "Can I book a private speed boat from Mumbai by boat name?",
    answer:
      "Yes. This page should display actual speed boat names from the database so guests can browse and book a specific boat directly.",
  },
  {
    question: "What guest capacities are available for speed boat booking?",
    answer:
      "You can explore speed boat options for 6, 8, 10, 12, 15 and 18 guests.",
  },
  {
    question: "Are speed boat transfers available from Mumbai to Mandwa and Alibaug?",
    answer:
      "Yes. Private speed boat transfers from Mumbai to Mandwa and Alibaug are available for guests looking for fast and convenient sea travel.",
  },
  {
    question: "Do you offer private speed boat trips to Elephanta?",
    answer:
      "Yes. Private speed boat trips from Mumbai to Elephanta are available for sightseeing, family visits and private island travel.",
  },
  {
    question: "Will each speed boat card have a direct booking link?",
    answer:
      "Yes. Each speed boat listing card should include a direct Book Now link along with a View Details option.",
  },
  {
    question: "What information should each speed boat card show?",
    answer:
      "Each card should show the boat name, image, guest capacity, route suitability, short description and booking link.",
  },
  {
    question: "Can I compare speed boats by guest capacity?",
    answer:
      "Yes. The page should help guests compare speed boat options based on capacity and route suitability.",
  },
  {
    question: "Is a private speed boat booking from Mumbai safe?",
    answer:
      "Yes. Indiaboats places strong emphasis on trained crew, guest support and responsible operations.",
  },
  {
    question: "How do I enquire if I am not sure which speed boat to choose?",
    answer:
      "You can contact Indiaboats through the enquiry form or WhatsApp with your date, number of guests and route.",
  },
];

const REGATTA_FAQS = [
  {
    question: "What is a corporate sailing regatta in Mumbai?",
    answer:
      "A corporate sailing regatta in Mumbai is a team-building sailing experience where employees or business groups take part in a structured on-water format designed to build teamwork, communication and leadership.",
  },
  {
    question: "How is a corporate sailing regatta different from a regular team outing?",
    answer:
      "A regular team outing is usually passive and social, while a sailing regatta is interactive and participation-led, which makes it more effective for team building and engagement.",
  },
  {
    question: "Why is a sailing regatta good for team building?",
    answer:
      "It naturally encourages communication, collaboration, decision-making, trust and shared achievement, which are all important team qualities.",
  },
  {
    question: "What happens during a corporate sailing regatta?",
    answer:
      "The experience may include briefing, team formation, guided sailing participation, regatta-style activity and shared team moments on the water.",
  },
  {
    question: "Is this suitable for leadership teams?",
    answer:
      "Yes. Corporate sailing regattas work very well for leadership groups because they highlight strategy, calm decision-making and teamwork in a live environment.",
  },
  {
    question: "What business goals can a corporate sailing regatta support?",
    answer:
      "It can support team building, employee engagement, leadership development, offsites, reward events and culture-building initiatives.",
  },
  {
    question: "How is Indiaboats different for corporate regattas?",
    answer:
      "Indiaboats focuses on premium on-water corporate experiences and helps companies create sailing regattas that feel structured, meaningful and memorable.",
  },
  {
    question: "How do I enquire about a corporate sailing regatta in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your preferred date, team size and event objective.",
  },
];

const CORPORATE_PARTIES_FAQS = [
  {
    question: "What is a corporate party on a yacht in Mumbai?",
    answer:
      "A corporate party on a yacht in Mumbai is a private company celebration on the water designed for employee engagement, recognition, milestones or festive gatherings.",
  },
  {
    question: "Is a yacht a good venue for an awards ceremony?",
    answer:
      "Yes. A yacht offers a more premium and memorable setting for awards ceremonies, recognition events and company milestones.",
  },
  {
    question: "What types of companies book yacht celebrations?",
    answer:
      "Startups, SMEs, large corporates, sales teams, leadership teams and businesses planning recognition or festive events can all use this format.",
  },
  {
    question: "What can happen during a corporate party or awards ceremony?",
    answer:
      "Companies often use the format for awards presentations, team celebrations, recognition moments, networking, festive gatherings and premium hosted events.",
  },
  {
    question: "How is Indiaboats different for corporate celebrations?",
    answer:
      "Indiaboats focuses on premium on-water experiences and helps companies create polished, memorable and professionally managed corporate events.",
  },
  {
    question: "Is this better than a hotel or banquet hall?",
    answer:
      "For many companies, yes. A yacht creates a more distinctive and memorable setting with stronger guest impression and better recall value.",
  },
  {
    question: "Can this be used for employee recognition and reward events?",
    answer:
      "Yes. Yacht-based celebrations are especially strong for employee recognition, team milestones and reward experiences.",
  },
  {
    question: "How do I enquire about a corporate party or awards ceremony in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your preferred date, guest count and event objective.",
  },
];

const CORPORATE_NETWORKING_FAQS = [
  {
    question: "What is a corporate networking event on a yacht in Mumbai?",
    answer:
      "A corporate networking event on a yacht in Mumbai is a premium business gathering on the water designed for client hosting, executive interaction, partner engagement and professional relationship-building.",
  },
  {
    question: "Why is a yacht good for business networking?",
    answer:
      "A yacht creates a more premium, memorable and private setting than a routine venue, which helps guests feel more engaged and leaves a stronger business impression.",
  },
  {
    question: "What is the difference between a corporate networking event and a regular business event?",
    answer:
      "A regular business event is usually hosted in a standard venue, while a networking event on a yacht turns the gathering into a premium hosted experience that feels more exclusive and more memorable.",
  },
  {
    question: "What happens during a corporate networking event on a yacht?",
    answer:
      "The event may include welcome hosting, networking, partner interaction, client conversations, executive engagement and premium business hospitality in a private setting.",
  },
  {
    question: "Is this suitable for client hosting and partner meetings?",
    answer:
      "Yes. Corporate networking events on a yacht work especially well for client appreciation, partner interaction and premium business relationship-building.",
  },
  {
    question: "What business goals can a corporate networking event support?",
    answer:
      "It can support client hosting, executive networking, partner engagement, investor interaction, business hospitality and stronger brand-led relationship-building.",
  },
  {
    question: "How is Indiaboats different for corporate networking events?",
    answer:
      "Indiaboats focuses on premium on-water corporate experiences and helps companies create networking events that feel polished, meaningful and memorable.",
  },
  {
    question: "How do I enquire about a corporate networking event in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your preferred date, guest profile and event objective.",
  },
];

const BIRTHDAY_FAQS = [
  {
    question: "What is a birthday yacht celebration in Mumbai like?",
    answer:
      "A birthday yacht celebration in Mumbai is a private on-water experience where you celebrate with cake, music, sea views and your own group in a more exclusive setting.",
  },
  {
    question: "Is a yacht a good venue for an anniversary celebration?",
    answer:
      "Yes. A yacht anniversary celebration feels romantic, scenic and private, making it a memorable option for couples and families.",
  },
  {
    question: "What can we do during a birthday or anniversary yacht celebration?",
    answer:
      "Guests often enjoy cake cutting, music, photos, private cruising, family time, sunset views and celebration moments with their group.",
  },
  {
    question: "Is this suitable for children and family groups?",
    answer:
      "Yes. Birthday and anniversary yacht celebrations can be suitable for children, adults, couples, families and multi-generation groups.",
  },
  {
    question: "Which yacht should I choose for up to 15 guests?",
    answer:
      "For up to 15 guests, a private yacht charter is usually the best option because it offers a more intimate and comfortable experience.",
  },
  {
    question: "Which yacht should I choose for more than 15 guests?",
    answer:
      "For more than 15 guests, a yacht party format is usually more suitable because it works better for larger celebration groups.",
  },
  {
    question: "How is Indiaboats different for birthday and anniversary celebrations?",
    answer:
      "Indiaboats focuses on premium on-water experiences, helping guests choose the right format and create celebrations that feel smooth, scenic and memorable.",
  },
  {
    question: "How do I enquire about a birthday or anniversary yacht celebration in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your date, guest count and celebration type.",
  },
];

const PRE_WEDDING_FAQS = [
  {
    question: "What is a pre-wedding yacht photoshoot in Mumbai like?",
    answer:
      "A pre-wedding yacht photoshoot in Mumbai is a private couple shoot on the water with sea views, elegant yacht backdrops and a more exclusive atmosphere.",
  },
  {
    question: "Is a yacht a good venue for a pre-wedding shoot?",
    answer:
      "Yes. A yacht offers a premium, romantic and scenic backdrop that makes couple photos feel more elegant and memorable.",
  },
  {
    question: "What can we capture during a yacht photoshoot?",
    answer:
      "Couples often capture portraits, candid moments, sunset shots, romantic videos, save-the-date content and engagement-style visuals.",
  },
  {
    question: "Is a private yacht better for a couple shoot?",
    answer:
      "Yes. For most couples, a private yacht charter creates a more intimate and relaxed environment for the shoot.",
  },
  {
    question: "Can a larger group join the photoshoot?",
    answer:
      "Yes. If your shoot includes a larger group or additional setup requirements, a bigger yacht option may be more suitable.",
  },
  {
    question: "How is Indiaboats different for pre-wedding yacht shoots?",
    answer:
      "Indiaboats focuses on premium on-water experiences and helps couples choose the right yacht format for a visually beautiful and smooth experience.",
  },
  {
    question: "What is the best time for a pre-wedding yacht photoshoot?",
    answer:
      "Many couples prefer golden hour and sunset because the natural light and sea backdrop create softer, more romantic images.",
  },
  {
    question: "How do I enquire about a pre-wedding yacht photoshoot in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your preferred date, expected group size and shoot requirements.",
  },
];

const FAMILY_FAQS = [
  {
    question: "What is a family yacht party in Mumbai like?",
    answer:
      "A family yacht party in Mumbai is a private on-water experience where families and close groups spend quality time together in a scenic and exclusive setting.",
  },
  {
    question: "Is a private yacht party suitable for family groups?",
    answer:
      "Yes. A private yacht party is ideal for family gatherings, private celebrations and meaningful time with loved ones.",
  },
  {
    question: "What can we do during a family yacht party?",
    answer:
      "Guests often enjoy private cruising, family time, photos, music, sunset views and celebration moments with their group.",
  },
  {
    question: "Which yacht should I choose for up to 15 guests?",
    answer:
      "For up to 15 guests, a private yacht charter is usually the best option because it feels more intimate and comfortable.",
  },
  {
    question: "Which yacht should I choose for more than 15 guests?",
    answer:
      "For more than 15 guests, a yacht party format is usually more suitable because it works better for larger private groups.",
  },
  {
    question: "Is this suitable for multi-generation family groups?",
    answer:
      "Yes. Family yacht parties can work very well for parents, children, grandparents and mixed-age groups.",
  },
  {
    question: "How is Indiaboats different for family and private celebrations?",
    answer:
      "Indiaboats focuses on premium on-water experiences and helps guests choose the right format for smooth, scenic and memorable private outings.",
  },
  {
    question: "How do I enquire about a family or private yacht party in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your date, guest count and occasion.",
  },
];

const PACKAGE_FAQS = [
  {
    question: "What is the difference between a package and a basic charter?",
    answer:
      "A curated package offers a more complete experience and may include route planning, food, beverages, transport, sightseeing or celebration elements, while a basic charter is usually only the boat booking.",
  },
  {
    question: "What kinds of packages are available?",
    answer:
      "Packages may include Mandwa trips, Elephanta outings, sailing experiences, private celebration packages and full-day charter formats.",
  },
  {
    question: "Can a package include premium food and beverages?",
    answer:
      "Yes. Depending on the package, premium food, beverages and other curated inclusions may be part of the experience.",
  },
  {
    question: "Are these packages directly bookable?",
    answer:
      "Yes. Each package card should include a direct booking option along with a details page.",
  },
  {
    question: "Who are these packages best suited for?",
    answer:
      "They are ideal for guests who want a more seamless, more premium and more fully planned on-water experience.",
  },
  {
    question: "Can I compare multiple package types on one page?",
    answer:
      "Yes. This page is designed to help guests browse and compare different curated package experiences in one place.",
  },
  {
    question: "Are there destination-based packages for Mandwa and Elephanta?",
    answer:
      "Yes. The page can include destination-led packages as well as sailing, celebration and charter-led formats.",
  },
  {
    question: "How do I choose the right package?",
    answer:
      "Choose based on your destination, group size, duration and the type of experience you want. Indiaboats can also help recommend the right package.",
  },
];

export const CONTACT_FAQS = [
  {
    question: "How do I book a yacht with Indiaboats?",
    answer:
      "Share your date, number of guests and preferred experience by phone, WhatsApp or enquiry form. Our team will recommend suitable options.",
  },
  {
    question: "Can you help me choose the right yacht or boat?",
    answer:
      "Yes. We can suggest options based on group size, occasion, destination and budget.",
  },
  {
    question: "Can I book speed boats to Mandwa or Elephanta?",
    answer:
      "Yes. Private speed boat options are available from Mumbai, subject to weather, sea conditions and availability.",
  },
  {
    question: "Do you handle corporate events?",
    answer:
      "Yes. Indiaboats organises corporate sailing regattas, yacht parties, networking events and customised corporate experiences.",
  },
];

export const ABOUT_FAQS = [
  {
    question: "What is Indiaboats?",
    answer:
      "Indiaboats is a yacht charter, boat rental and marine experience company offering private yacht charters, sailing experiences, speed boat transfers, yacht parties, curated packages and corporate events in Mumbai and Goa.",
  },
  {
    question: "When was Indiaboats founded?",
    answer:
      "Indiaboats was founded in August 2016 with the aim of creating safer, smoother and more memorable experiences on the water.",
  },
  {
    question: "Who founded Indiaboats?",
    answer:
      "Indiaboats was founded by Mehul Ashar, an experienced sailor with more than 35 years at sea, a decorated national sailing champion who has represented India at prestigious Asian Sailing Regattas and World Championships.",
  },
  {
    question: "Where does Indiaboats operate?",
    answer:
      "Indiaboats offers yacht, sailing and boat experiences primarily in Mumbai and Goa, including private trips to Mandwa, Alibaug and Elephanta from Mumbai.",
  },
  {
    question: "What types of yachts and boats does Indiaboats offer?",
    answer:
      "Indiaboats offers sail boats, compact yachts, mid size yachts, premium yachts, bigger yachts, party yachts, catamarans and speed boats for different group sizes and occasions.",
  },
  {
    question: "Is guest safety important at Indiaboats?",
    answer:
      "Yes. Guest safety is a central part of every Indiaboats experience. Operations are planned around experienced crew, vessel suitability, weather, sea conditions and captain safety clearance.",
  },
  {
    question: "Does Indiaboats arrange private celebrations and corporate events?",
    answer:
      "Yes. Indiaboats arranges private celebrations, birthdays, anniversaries, proposals, pre-wedding shoots, family outings, yacht parties, corporate sailing regattas, networking events and company celebrations.",
  },
  {
    question: "Why choose Indiaboats?",
    answer:
      "Indiaboats combines an extensive fleet, experienced marine operations, personalised planning and a strong focus on safety, comfort and memorable guest experiences.",
  },
];

export const ALIBAUG_ELEPHANTA_FAQS = [
  {
    question: "How do I travel from Mumbai to Alibaug by private boat?",
    answer:
      "Private speed boats operate from Mumbai to Mandwa. From Mandwa, guests can continue by road to Alibaug and nearby destinations.",
  },
  {
    question: "Can I book a private speed boat from Mumbai to Mandwa?",
    answer:
      "Yes. Indiaboats offers private speed boats in multiple seating capacities for Mumbai to Mandwa travel.",
  },
  {
    question: "Can I travel privately from Mumbai to Elephanta Island?",
    answer:
      "Yes. Private speed boat options are available for Elephanta trips, subject to operating conditions and permissions.",
  },
  {
    question: "How many people can travel on a speed boat?",
    answer:
      "Indiaboats offers boats for groups ranging from approximately 5 guests to 20 guests, with larger-capacity options available for bigger groups.",
  },
  {
    question: "Can Indiaboats arrange a full-day Alibaug package?",
    answer:
      "Yes. Curated Mandwa and Alibaug packages can include private boat transfers and local transportation.",
  },
  {
    question: "Do you offer guided Elephanta packages?",
    answer:
      "Curated Elephanta packages can include private boat transfers, required permissions and a local guide.",
  },
  {
    question: "Are boat trips available throughout the year?",
    answer:
      "Operations are subject to weather, monsoon restrictions, sea conditions, government regulations and captain safety clearance.",
  },
  {
    question: "Where can I book the speed boat?",
    answer:
      "You can view available boat sizes and booking options on the Indiaboats Speed Boat Transfer page.",
  },
];

const CORPORATE_EVENTS_FAQS = [
  {
    question: "What types of corporate events does Indiaboats offer?",
    answer:
      "Indiaboats offers corporate sailing regattas, corporate yacht parties and corporate networking events in Mumbai.",
  },
  {
    question: "What is a corporate sailing regatta?",
    answer:
      "A corporate sailing regatta is a team-building sailing experience where employees or business groups participate in a structured on-water activity designed to build teamwork, leadership and communication.",
  },
  {
    question: "Is a yacht a good venue for a corporate party?",
    answer:
      "Yes. A yacht offers a more premium, private and memorable setting for company celebrations, annual gatherings and employee recognition events.",
  },
  {
    question: "Why choose a yacht for a networking event?",
    answer:
      "A yacht creates a stronger business impression and offers a more distinctive environment for hosting clients, partners and executive guests.",
  },
  {
    question: "Which corporate format is best for team building?",
    answer:
      "Corporate sailing regattas are usually the best choice for team building because they encourage active participation and collaboration.",
  },
  {
    question: "Which corporate format is best for company celebrations?",
    answer:
      "Corporate yacht parties are usually the best choice for annual parties, festive gatherings and recognition-led events.",
  },
  {
    question: "Which corporate format is best for client hosting and networking?",
    answer:
      "Corporate networking events on a yacht are usually the best fit for client hosting, partner interaction and premium business hospitality.",
  },
  {
    question: "How do I enquire about a corporate event in Mumbai?",
    answer:
      "You can enquire through WhatsApp or the contact form and share your preferred date, guest count and event objective.",
  },
];

const serviceAndFaq = (serviceType, description, faqs) => [
  buildServiceSchema(serviceType, description),
  buildFaqSchema(faqs),
];

export const PAGE_SCHEMAS = {
  home: LOCAL_BUSINESS_SCHEMA,
  about: buildFaqSchema(ABOUT_FAQS),
  contact: [LOCAL_BUSINESS_SCHEMA, buildFaqSchema(CONTACT_FAQS)],
  packages: serviceAndFaq(
    "Curated Yacht & Boat Packages",
    "Browse curated packages — Mandwa day trips, Elephanta Island tours, premium sailing and sunset sails. Complete end-to-end experiences.",
    PACKAGE_FAQS
  ),
  privateYachtCharter: serviceAndFaq(
    "Private Yacht Charter",
    "Book a private yacht charter in Mumbai or Goa for up to 15 guests. Compare Luxury and Explore fleets, pricing and inclusions.",
    PRIVATE_YACHT_FAQS
  ),
  yachtPartyMumbai: serviceAndFaq(
    "Yacht Party Charter",
    "Host a yacht party in Mumbai for 15 or more guests. Large party yachts for birthdays, celebrations and group events.",
    YACHT_PARTY_FAQS
  ),
  speedBoat: serviceAndFaq(
    "Speed Boat Transfer",
    "Book a private or shared speed boat transfer from Mumbai to Alibaug or Mandwa. Quick, comfortable, reliable — skip the road traffic.",
    SPEED_BOAT_FAQS
  ),
  corporateSailingRegatta: serviceAndFaq(
    "Corporate Sailing Regatta",
    "Run a corporate sailing regatta in Mumbai for team building, leadership training or client entertainment. Custom B2B packages.",
    REGATTA_FAQS
  ),
  corporatePartyAwards: serviceAndFaq(
    "Corporate Party & Awards Yacht",
    "Celebrate company milestones, awards nights and corporate parties on a private yacht in Mumbai — a more memorable setting than a banquet hall.",
    CORPORATE_PARTIES_FAQS
  ),
  corporateEventsNetworking: serviceAndFaq(
    "Corporate Yacht Networking Event",
    "Host a corporate yacht event in Mumbai for client entertainment, business networking or team celebrations. Full event coordination.",
    CORPORATE_NETWORKING_FAQS
  ),
  birthdayAnniversary: serviceAndFaq(
    "Birthday & Anniversary Yacht Celebration",
    "Celebrate a birthday or anniversary on a private yacht in Mumbai — cake-cutting, music, sunset views and photos.",
    BIRTHDAY_FAQS
  ),
  preWedding: serviceAndFaq(
    "Pre-Wedding Yacht Photoshoot",
    "Book a private yacht for your pre-wedding photoshoot in Mumbai. Stunning sea backdrops and skyline views for a romantic shoot.",
    PRE_WEDDING_FAQS
  ),
  familyPrivateYachtParty: serviceAndFaq(
    "Family Yacht Party",
    "Plan a private family yacht outing in Mumbai — away from the crowds, with comfort, privacy and sea views.",
    FAMILY_FAQS
  ),
  corporateEvents: [
    buildServiceSchema(
      "Corporate Yacht Events",
      "Plan corporate yacht events, sailing regattas, networking experiences, awards evenings and company celebrations in Mumbai with Indiaboats. Premium on-water events with smooth planning and guest safety."
    ),
    buildFaqSchema(CORPORATE_EVENTS_FAQS),
    buildBreadcrumbSchema([
      { name: "Home", url: "https://www.indiaboats.in/" },
      {
        name: "Corporate Events",
        url: "https://www.indiaboats.in/CorporateEvents",
      },
    ]),
    ORGANIZATION_SCHEMA,
  ],
  alibaugElephantaBoatTours: [
    buildServiceSchema(
      "Alibaug & Elephanta Boat Tours",
      "Book private boat trips from Mumbai to Alibaug, Mandwa and Elephanta with Indiaboats. Enjoy fast speed boat transfers, scenic island escapes and curated day experiences."
    ),
    buildFaqSchema(ALIBAUG_ELEPHANTA_FAQS),
    buildBreadcrumbSchema([
      { name: "Home", url: "https://www.indiaboats.in/" },
      {
        name: "Alibaug & Elephanta Boat Tours",
        url: "https://www.indiaboats.in/alibaug-elephanta-boat-tours-mumbai",
      },
    ]),
  ],
};
